import { Link } from "react-router-dom";
import { MapPin, MessageCircle, ClipboardList, ArrowRight } from "lucide-react";
import { Eyebrow, Reveal } from "@/components/site/Reveal";
import { WhatsAppButton, RequestButton } from "@/components/site/Buttons";
import { usePageMeta } from "@/lib/usePageMeta";
import { BUSINESS, whatsappLink } from "@/content/site";

const Panel = ({ icon: Icon, label, children, testId }) => (
  <div className="metal metal-edge chamfer border border-[var(--line)] p-7 md:p-10 h-full" data-testid={testId}>
    <p className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.2em] text-[var(--mute)]">
      <Icon className="h-4 w-4 text-[var(--red)]" aria-hidden /> {label}
    </p>
    {children}
  </div>
);

export default function Contact() {
  usePageMeta(
    "Contact NE Tool Repair | Power Tool Repair Newcastle & North East",
    "Contact NE Tool Repair on WhatsApp or send a repair enquiry. Milwaukee M12 & M18 power tool repair serving Newcastle upon Tyne and the North East.",
    "/contact"
  );
  return (
    <div data-testid="contact-page">
      <section className="relative border-b border-[var(--line)] overflow-hidden">
        <div className="absolute inset-0 grid-lines [mask-image:linear-gradient(to_bottom,black,transparent)]" aria-hidden />
        <div className="relative mx-auto max-w-[1400px] px-5 md:px-10 pt-16 md:pt-24 pb-14 md:pb-20">
          <Reveal>
            <Eyebrow>Get in touch</Eyebrow>
            <h1 className="mt-6 font-display font-black uppercase text-6xl sm:text-7xl lg:text-[8rem] leading-[0.86]">Contact</h1>
            <p className="mt-6 max-w-xl text-base md:text-lg text-[var(--grey)] leading-relaxed">The quickest way to reach NE Tool Repair is WhatsApp. You can send photos of the tool and the fault there too.</p>
          </Reveal>
        </div>
      </section>

      <section className="border-b border-[var(--line)] py-14 md:py-20">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 grid lg:grid-cols-12 gap-4">
          <Reveal className="lg:col-span-7">
            <Panel icon={MessageCircle} label="WhatsApp" testId="contact-whatsapp-panel">
              <a href={whatsappLink} target="_blank" rel="noopener noreferrer" className="mt-8 block font-display font-black text-5xl sm:text-6xl md:text-7xl leading-none hover:text-[var(--red-hi)] transition-colors" data-testid="contact-whatsapp-number">
                {BUSINESS.whatsappDisplay}
              </a>
              <p className="mt-5 text-sm md:text-base text-[var(--grey)] leading-relaxed max-w-md">Message us with the tool brand, model and what's going wrong. Photos help us understand the problem.</p>
              <WhatsAppButton testId="contact-whatsapp-btn" className="mt-8 w-full sm:w-auto" />
            </Panel>
          </Reveal>
          <Reveal className="lg:col-span-5" delay={0.08}>
            <Panel icon={ClipboardList} label="Repair enquiry form" testId="contact-form-panel">
              <h2 className="mt-8 font-display font-extrabold uppercase text-3xl md:text-4xl leading-[0.95]">Prefer to fill in a form?</h2>
              <p className="mt-4 text-sm md:text-base text-[var(--grey)] leading-relaxed">Send your details and the fault through our repair form, and we'll reply using the contact method you choose.</p>
              <RequestButton testId="contact-request-btn" className="mt-8 w-full sm:w-auto" />
            </Panel>
          </Reveal>
        </div>
      </section>

      <section className="relative py-20 md:py-28 border-b border-[var(--line)] overflow-hidden" data-testid="contact-area-section">
        <img src="/images/bench.jpg" alt="" aria-hidden className="absolute inset-0 h-full w-full object-cover opacity-[0.15] grayscale" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-b from-[var(--ink)] via-[var(--ink)]/70 to-[var(--ink)]" aria-hidden />
        <div className="relative mx-auto max-w-[1400px] px-5 md:px-10 grid lg:grid-cols-12 gap-10 items-end">
          <Reveal className="lg:col-span-8">
            <p className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.2em] text-[var(--mute)]"><MapPin className="h-4 w-4 text-[var(--red)]" aria-hidden /> Service area</p>
            <h2 className="mt-6 font-display font-black uppercase leading-[0.86] text-[13vw] md:text-8xl">Newcastle<br /><span className="stroke-text">&amp; the</span> North East</h2>
          </Reveal>
          <Reveal className="lg:col-span-4" delay={0.1}>
            <p className="text-sm md:text-base text-[var(--grey)] leading-relaxed">Based in Newcastle upon Tyne and helping tool owners across the North East of England. We don't list a workshop address online. We'll agree how to get your tool to us when we reply to your enquiry.</p>
            <Link to="/repairs-services" className="group mt-6 inline-flex items-center gap-3 font-display font-extrabold uppercase tracking-[0.14em] text-sm border-b-2 border-[var(--red)] pb-1" data-testid="contact-services-link">
              See repairs &amp; services <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" aria-hidden />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
